import React, { useContext } from "react";
import { createPortal } from "react-dom";
import { GapDensityMapDataContext, getBuckets } from "@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapDataContext";
import { MapCustomControlProps } from "@app/shared/mapping/MapCustomControlProps";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";
import { GapDensityMapThemeContext } from "@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapThemeContext";

const GapDensityMapLegendControl: React.FC<MapCustomControlProps> = ({ position, ignoreInMapExport = false }) => {
  const mapDataContext = useContext(GapDensityMapDataContext);
  const themeContext = useContext(GapDensityMapThemeContext);

  const container = useMapboxControl({ mapRef: mapDataContext.mapRef, position: position ?? "top-right" });

  const filters = mapDataContext.filters || [];
  const specialtyFilter = filters.find(
    (f: { filterType: string; filterValue: string }) =>
      f.filterType?.toLowerCase().includes("specialty") && f.filterValue
  );

  let buckets: number[][] = [];
  let colors: string[] = [];
  let bySpecialty = false;

  if (specialtyFilter) {
    buckets = [[0, 0], [1, 1]];
    colors = themeContext.getGapDensityMapColors(2);
    bySpecialty = true;
  } else {
    let specialtiesCount = 0;
    const countyMaps = mapDataContext.countyMaps;
    if (countyMaps && countyMaps.features && countyMaps.features.length > 0) {
      const firstFeature = countyMaps.features[0];
      if (firstFeature.properties && Array.isArray(firstFeature.properties.specialties)) {
        specialtiesCount = firstFeature.properties.specialties.length;
      }
    }
    if (specialtiesCount > 0) {
      buckets = getBuckets(specialtiesCount);
      colors = themeContext.getGapDensityMapColors(buckets.length);
    }
  }

  const removeLegend = buckets.length === 0 || colors.length === 0;
  if (removeLegend || !container) return null;

  const border = "1px solid #ffffff";
  const labels: React.ReactNode[] = [];

  if (bySpecialty) {
    labels.push(
      <div key="0">
        <i
          style={{
            backgroundColor: colors[0],
            border,
            display: "inline-block",
            height: 18,
            marginRight: 8,
            verticalAlign: "middle",
            width: 18,
          }}
        />
        <span>0</span>
      </div>
    );
    labels.push(
      <div key="1">
        <i
          style={{
            backgroundColor: colors[colors.length - 1],
            border,
            display: "inline-block",
            height: 18,
            marginRight: 8,
            verticalAlign: "middle",
            width: 18,
          }}
        />
        <span>1</span>
      </div>
    );
  } else {
    for (let i = 0; i < buckets.length; i++) {
      const low = buckets[i][0];
      const high = buckets[i][1];
      labels.push(
        <div key={i}>
          <i
            style={{
              backgroundColor: colors[i],
              border,
              display: "inline-block",
              height: 18,
              marginRight: 8,
              verticalAlign: "middle",
              width: 18,
            }}
          />
          <span>
            {low}
            {i === 0 || low === high ? "" : ` – ${high}`}
          </span>
        </div>
      );
    }
  }

  return createPortal(
    <div {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      <div
        className="legend p-2"
        style={{
          background: "rgba(255,255,255,0.9)",
          borderRadius: 8,
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          minWidth: 160,
          zIndex: 1000,
        }}
      >
        <h6 style={{ margin: 0 }}>Map: Legend</h6>
        <p style={{ margin: "4px 0 8px 0" }}>Gap Count per County</p>
        {labels}
      </div>
    </div>,
    container
  );
};

export default GapDensityMapLegendControl;
